import { secureFetch } from "@utils/apiClient";
import React, { useState } from 'react';
import { ClipboardCheck, ShieldAlert, ArrowRight, ZapOff, Loader2 } from 'lucide-react';

/**
 * MICHEAL'S TECHNICAL DESIGN - DISCOVERY PORTAL
 * VERSION: 1.2.0 (Logic-Bridge Ready)
 * ROLE: Converts inquiries into scored forensic briefs via the Sales Engine Proxy.
 */
const DiscoveryPortal = () => {
    const [step, setStep] = useState(1);
    const [complexity, setComplexity] = useState(0);
    const [serviceType, setServiceType] = useState('');
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        description: ''
    });

    const handleIntake = (value, label) => {
        setComplexity(prev => prev + value);
        setServiceType(label);
        setStep(2);
    };

    const handleInitiateDiscovery = async () => {
        setLoading(true);
        try {
            // TARGETING THE VERIFIED PROXY GATEWAY (PORT 5002)
            const response = await secureFetch('http://localhost:5002/api/proxy/intake', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    serviceType: serviceType,
                    description: formData.description || `Complexity Score: ${complexity}% - Client initiated discovery session.`
                })
            });

            const data = await response.json();

            if (data.success) {
                // Redirect to the checkout or confirmation page upon successful vault entry
                window.location.href = '/book-consultation';
            } else {
                console.error("Institutional Access Refused:", data.error);
                alert("The secure gateway is currently unresponsive. Please attempt again shortly.");
            }
        } catch (error) {
            console.error("Critical Proxy Link Failure:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto my-20 bg-white border border-stone-200 shadow-2xl overflow-hidden rounded-sm animate-in fade-in duration-700">
            {/* 🏛️ HEADER: Establishing Professional Authority */}
            <div className="bg-stone-900 p-8 text-center">
                <h2 className="text-white font-serif italic text-2xl tracking-tight">Phase 01: Forensic Discovery</h2>
                <p className="text-stone-400 text-[10px] uppercase tracking-[0.4em] mt-2">Initial Architectural Assessment Gate</p>
            </div>

            <div className="p-12">
                {step === 1 && (
                    <>
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                            <div className="border-b border-stone-100 pb-4 flex items-end justify-between">
                                <div>
                                    <h3 className="text-sm font-bold uppercase tracking-widest text-stone-900">01. Project Scope Analysis</h3>
                                    <p className="text-[10px] text-stone-400 mt-1 uppercase tracking-widest">Identifying Systemic Parameters</p>
                                </div>
                                <ClipboardCheck size={20} className="text-stone-300 mb-1" />
                            </div>
                        </div>
                        
                        <p className="text-xs text-stone-500 leading-relaxed mt-8">
                            Select the primary nature of this technical consultation to initialize the complexity index:
                        </p>

                        <div className="grid grid-cols-1 gap-4 mt-8">
                            <OnboardingButton 
                                label="New System Architecture (Design Only)" 
                                sub="High Complexity / Strategic Planning" 
                                onClick={() => handleIntake(40, "New System Architecture")} 
                            />
                            <OnboardingButton 
                                label="Forensic Failure Analysis" 
                                sub="Diagnostic Oversight / Technical Audit" 
                                onClick={() => handleIntake(60, "Forensic Failure Analysis")} 
                            />
                            <OnboardingButton 
                                label="System Optimization Review" 
                                sub="Efficiency Audit / Architectural Updates" 
                                onClick={() => handleIntake(20, "System Optimization Review")} 
                            />
                        </div>
                    </>
                )}

                {step === 2 && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 text-center">
                        <ShieldAlert size={40} className="mx-auto text-amber-600 mb-4" />
                        
                        <div>
                            <h3 className="text-sm font-bold uppercase tracking-widest text-stone-900">
                                Complexity Score: {complexity}%
                            </h3>
                            <div className="w-full h-1 bg-stone-100 mt-2 max-w-[200px] mx-auto rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-amber-600 transition-all duration-1000" 
                                    style={{ width: `${complexity}%` }}
                                />
                            </div>
                        </div>

                        <div className="space-y-4 max-w-sm mx-auto">
                            <input 
                                type="text"
                                placeholder="Full Name"
                                className="w-full border-b border-stone-200 py-2 text-xs focus:border-stone-900 outline-none transition-colors"
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                            />
                            <input 
                                type="email"
                                placeholder="Institutional Email"
                                className="w-full border-b border-stone-200 py-2 text-xs focus:border-stone-900 outline-none transition-colors"
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                            />
                        </div>

                        <p className="text-xs text-stone-500 leading-relaxed max-w-md mx-auto font-light">
                            Based on your parameters, this project requires a <span className="text-stone-900 font-bold">Principal-Level Forensic Review</span>. 
                            To proceed with the technical brief and schematic audit, you must authorize the Phase 01 Discovery Retainer.
                        </p>
                        
                        <div className="bg-stone-50 p-6 border border-dashed border-stone-300 space-y-4">
                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-stone-600">
                                <span>Phase 01 Retainer Allocation</span>
                            </div>
                            <button 
                                onClick={handleInitiateDiscovery}
                                disabled={loading || !formData.name || !formData.email}
                                className="w-full bg-stone-900 text-white py-4 text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-stone-800 disabled:bg-stone-300 transition-all flex items-center justify-center gap-3 shadow-xl"
                            >
                                {loading ? <Loader2 size={14} className="animate-spin" /> : <>Initiate Discovery <ArrowRight size={14} /></>}
                            </button>
                        </div>
                        
                        <div className="flex justify-center items-center gap-2 text-stone-400 pt-4">
                            <ZapOff size={12} />
                            <p className="text-[9px] uppercase tracking-widest italic font-medium">
                                Technical Consultation Only • No Physical Labor or Hardware Installation
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const OnboardingButton = ({ label, sub, onClick }) => (
  <button 
    onClick={onClick}
    className="group text-left p-6 border border-stone-100 hover:border-stone-900 hover:bg-stone-50 transition-all duration-300 flex justify-between items-center w-full"
  >
    <div>
        <p className="text-xs font-bold text-stone-900 group-hover:translate-x-1 transition-transform">{label}</p>
        <p className="text-[10px] text-stone-400 mt-1 uppercase tracking-tighter">{sub}</p>
    </div>
    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400" />
  </button>
);

export default DiscoveryPortal;